import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import CommonDatePicker from '../../../../components/DatePicker/CommonDatePicker';
import OrgTreeSelect from '../../../../components/OrgSelect/OrgTreeSelect';
import { saveSafetyProject, getSafetyBasicInfo, updateSafetyProjectStatus, deleteSafetyProject } from '../../../../api/safetyAssessApi';

// 상태 박스 스타일 유틸리티
const getStatusBoxStyle = (status, isEdit = false) => {
  const base = {
    padding: '10px 14px',
    borderRadius: '8px',
    height: '42px',
    display: 'flex',
    alignItems: 'center',
    fontSize: '0.84rem',
    fontWeight: 700,
    boxSizing: 'border-box'
  };

  switch (status) {
    case 'in_progress':
      return { ...base, background: '#e0f2fe', border: '1px solid #bae6fd', color: '#0369a1' };
    case 'completed':
      return { ...base, background: '#d1fae5', border: '1px solid #a7f3d0', color: '#047857' };
    case 'canceled':
      return { ...base, background: '#fef3c7', border: '1px solid #fde68a', color: '#b45309' };
    case 'deleted':
      return { ...base, background: '#fee2e2', border: '1px solid #fca5a5', color: '#b91c1c' };
    default:
      return { ...base, background: isEdit ? '#f1f5f9' : '#f8fafc', border: `1px solid ${isEdit ? '#cbd5e1' : '#e2e8f0'}`, color: '#475569' };
  }
};

const getStatusLabel = (status) => {
  switch (status) {
    case 'in_progress': return '● 진행중';
    case 'completed': return '● 완료';
    case 'canceled': return '● 취소';
    case 'deleted': return '● 삭제';
    default: return '-';
  }
};

/**
 * SafetyProjectModal
 * 
 * @param {boolean} show - 모달 표시 여부
 * @param {'create'|'edit'|'view'} mode - 모달 모드 (신규등록/수정/조회)
 * @param {object|null} initialData - 수정/조회 시 기존 프로젝트 데이터
 * @param {string} token - 인증 토큰
 * @param {boolean} hasPermission - 현재 사용자의 수정/삭제 권한 여부
 * @param {function} onClose - 모달 닫기 콜백
 * @param {function} onSaved - 저장/상태변경 성공 후 콜백 (부모가 리스트 새로고침)
 * @param {function} onEdit - 조회 모달에서 [수정] 클릭 시 콜백 (부모에서 mode를 edit로 전환)
 */
function SafetyProjectModal({ show, mode = 'create', initialData = null, token, hasPermission = false, onClose, onSaved, onEdit }) {
  // 폼 데이터 상태
  const [formData, setFormData] = useState({
    name: '', type: '정기', reason: '', period: '', startDate: '', endDate: '', workerCnt: '', siteName: '', managerId: '', managerName: '', status: ''
  });
  const [errorMessage, setErrorMessage] = useState('');

  // 조직도 팝업
  const [showOrgPopup, setShowOrgPopup] = useState(false);

  // 모달이 열릴 때 초기 데이터 세팅
  useEffect(() => {
    if (!show) return;

    if (mode === 'create') {
      // 신규 등록: 사업장 기본정보 자동 로드
      const loadBasicInfo = async () => {
        try {
          const basicInfo = await getSafetyBasicInfo(token);
          setFormData({
            name: '', type: '정기', reason: '', period: '', startDate: '', endDate: '', workerCnt: '',
            siteName: basicInfo?.siteName || '',
            managerName: basicInfo?.managerName || '',
            managerId: basicInfo?.managerId || '',
            status: 'in_progress'
          });
        } catch (e) {
          console.error(e);
          setFormData({ name: '', type: '정기', reason: '', period: '', startDate: '', endDate: '', workerCnt: '', siteName: '', managerId: '', managerName: '', status: 'in_progress' });
        }
      };
      loadBasicInfo();
    } else if (mode === 'edit' && initialData) {
      // 수정 모드: 기존 데이터 바인딩
      let startDate = '';
      let endDate = '';
      if (initialData.period) {
        const dates = initialData.period.split('~').map(d => d.trim().replace(/\./g, '-'));
        if (dates.length === 2) {
          startDate = dates[0];
          endDate = dates[1];
        }
      }
      setFormData({
        id: initialData.id,
        name: initialData.name,
        type: initialData.type || '정기',
        reason: initialData.reason || '',
        period: initialData.period || '',
        startDate,
        endDate,
        workerCnt: initialData.workerCnt || '',
        siteName: initialData.siteName || '',
        managerName: initialData.managerName || '',
        managerId: initialData.managerId || '',
        status: initialData.status || 'in_progress'
      });
    }
  }, [show, mode, initialData, token]);

  // 저장 핸들러
  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.name) return alert('위험성평가명을 입력하세요.');
    try {
      const payload = { ...formData };
      if (payload.startDate && payload.endDate) {
        payload.period = `${payload.startDate} ~ ${payload.endDate}`;
      }
      await saveSafetyProject(payload, token);
      if (onSaved) onSaved();
      if (onClose) onClose();
      alert(formData.id ? '위험성평가 정보가 수정되었습니다.' : '신규 위험성평가 프로젝트가 생성되었습니다.');
    } catch (err) {
      alert('저장 실패');
    }
  };

  // OrgTreeSelect 담당자 확인 핸들러
  const handleOrgConfirm = (selectedTargets) => {
    if (selectedTargets && selectedTargets.length > 0) {
      const target = selectedTargets[0];
      const name = target.target_name || '';
      const pos = target.position || '';
      const dept = target.dept_name || '';
      const formattedName = `${name}${pos ? ' ' + pos : ''}${dept ? '(' + dept + ')' : ''}`;
      setFormData(prev => ({
        ...prev,
        managerId: target.target_id,
        managerName: formattedName
      }));
    }
    setShowOrgPopup(false);
  };

  // 상태 변경 핸들러 (조회 모달용)
  const handleStatusChange = async (id, newStatus, label) => {
    setErrorMessage('');
    if (!window.confirm(`이 프로젝트를 [${label}] 처리하시겠습니까?`)) return;
    try {
      await updateSafetyProjectStatus(id, newStatus, token);
      if (onSaved) onSaved();
      if (onClose) onClose();
      alert(`${label} 처리되었습니다.`);
    } catch (err) {
      setErrorMessage(err.response?.data?.message || '상태 변경 실패');
    }
  };

  // 삭제 핸들러 (조회 모달용)
  const handleDelete = async (id) => {
    if (!window.confirm('해당 위험성평가 프로젝트를 삭제하시겠습니까?')) return;
    try {
      await deleteSafetyProject(id, token);
      if (onSaved) onSaved();
      if (onClose) onClose();
    } catch (err) {
      alert('삭제 실패');
    }
  };

  if (!show) return null;

  // ===== 조회 모드 =====
  if (mode === 'view' && initialData) {
    return ReactDOM.createPortal(
      <div
        style={{
          position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
          backgroundColor: 'rgba(15, 23, 42, 0.7)', backdropFilter: 'blur(6px)',
          zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px'
        }}
        onClick={onClose}
      >
        <div
          style={{
            position: 'relative',
            width: '100%', maxWidth: '880px', maxHeight: '90vh',
            display: 'flex', flexDirection: 'column',
            background: '#fff', borderRadius: 'var(--sf-radius-xl)',
            boxShadow: '0 25px 50px rgba(0,0,0,0.25)', overflow: 'hidden'
          }}
          onClick={e => e.stopPropagation()}
        >
          {/* 헤더 */}
          <div style={{
            background: 'var(--sf-gradient-primary)', padding: '24px 28px',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            color: '#fff'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <h4 style={{ fontWeight: 800, margin: 0, fontSize: '1.3rem' }}>위험성평가 상세 조회</h4>
              <span className="sf-badge sf-badge-white" style={{ display: 'inline-block' }}>정보 조회</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, fontSize: '0.9rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <i className="fas fa-user-edit"></i>
                <span>{initialData?.writeName || initialData?.drafterName || initialData?.managerName || '작성자 미상'}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <i className="far fa-clock"></i>
                <span>
                  {(initialData?.createdAt || initialData?.created_at) ? (() => {
                    const d = new Date(initialData.createdAt || initialData.created_at);
                    const y = d.getFullYear();
                    const m = String(d.getMonth() + 1).padStart(2, '0');
                    const day = String(d.getDate()).padStart(2, '0');
                    const h = String(d.getHours()).padStart(2, '0');
                    const min = String(d.getMinutes()).padStart(2, '0');
                    const sec = String(d.getSeconds()).padStart(2, '0');
                    return `${y}-${m}-${day} ${h}:${min}:${sec}`;
                  })() : '-'}
                </span>
              </div>
              <div style={{ width: '1px', height: '16px', background: 'rgba(255,255,255,0.3)', margin: '0 4px' }}></div>
              <button
                onClick={onClose}
                style={{
                  background: 'transparent', border: 'none',
                  cursor: 'pointer', color: '#fff', fontSize: '1.2rem',
                  display: 'flex', alignItems: 'center'
                }}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
          </div>

          {/* 본문 */}
          <div style={{ padding: '28px', overflowY: 'auto' }}>
            <div className="row g-4">
              {/* 평가 구분 */}
              <div className="col-md-4">
                <label style={{ fontSize: '0.88rem', fontWeight: 800, color: '#0f172a', marginBottom: 8, display: 'block' }}>
                  <i className="fas fa-tag" style={{ color: 'var(--sf-primary)', marginRight: 6 }}></i>평가 구분
                </label>
                <div style={{ padding: '10px 14px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: '0.84rem', color: '#475569', fontWeight: 500 }}>
                  {initialData.type}평가
                </div>
              </div>

              {/* 사업장명 */}
              <div className="col-md-8">
                <label style={{ fontSize: '0.88rem', fontWeight: 800, color: '#0f172a', marginBottom: 8, display: 'block' }}>
                  <i className="fas fa-building" style={{ color: 'var(--sf-primary)', marginRight: 6 }}></i>사업장명
                </label>
                <div style={{ padding: '10px 14px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: '0.84rem', color: '#475569', fontWeight: 500 }}>
                  {initialData.siteName || '-'}
                </div>
              </div>

              {/* 위험성평가명 */}
              <div className="col-md-9">
                <label style={{ fontSize: '0.88rem', fontWeight: 800, color: '#0f172a', marginBottom: 8, display: 'block' }}>
                  <i className="fas fa-clipboard-list" style={{ color: 'var(--sf-primary)', marginRight: 6 }}></i>위험성평가명
                </label>
                <div style={{ padding: '10px 14px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: '0.84rem', color: '#475569', fontWeight: 500, height: '42px', display: 'flex', alignItems: 'center' }}>
                  {initialData.name}
                </div>
              </div>

              {/* 진행 상태 */}
              <div className="col-md-3">
                <label style={{ fontSize: '0.88rem', fontWeight: 800, color: '#0f172a', marginBottom: 8, display: 'block' }}>
                  <i className="fas fa-info-circle" style={{ color: 'var(--sf-primary)', marginRight: 6 }}></i>진행 상태
                </label>
                <div style={getStatusBoxStyle(initialData.status, false)}>
                  {getStatusLabel(initialData.status)}
                </div>
              </div>

              {/* 평가 실시 기간 */}
              <div className="col-md-8">
                <label style={{ fontSize: '0.88rem', fontWeight: 800, color: '#0f172a', marginBottom: 8, display: 'block' }}>
                  <i className="far fa-calendar-alt" style={{ color: 'var(--sf-primary)', marginRight: 6 }}></i>평가 실시 기간
                </label>
                <div style={{ padding: '10px 14px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: '0.84rem', color: '#475569', fontWeight: 500 }}>
                  {initialData.period ? initialData.period.replace(/\./g, '-') : '-'}
                </div>
              </div>

              {/* 참여 근로자 수 */}
              <div className="col-md-4">
                <label style={{ fontSize: '0.88rem', fontWeight: 800, color: '#0f172a', marginBottom: 8, display: 'block' }}>
                  <i className="fas fa-users" style={{ color: 'var(--sf-primary)', marginRight: 6 }}></i>참여 근로자 수 (명)
                </label>
                <div style={{ padding: '10px 14px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: '0.84rem', color: '#475569', fontWeight: 500 }}>
                  {initialData.workerCnt ? `${initialData.workerCnt}명` : '-'}
                </div>
              </div>

              {/* 안전관리자 */}
              <div className="col-md-6">
                <label style={{ fontSize: '0.88rem', fontWeight: 800, color: '#0f172a', marginBottom: 8, display: 'block' }}>
                  <i className="fas fa-id-badge" style={{ color: 'var(--sf-primary)', marginRight: 6 }}></i>안전관리자 (평가 총괄 담당자)
                </label>
                <div style={{ padding: '10px 14px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: '0.84rem', color: '#475569', fontWeight: 500 }}>
                  {initialData.managerName ? initialData.managerName.replace(/\s*\/?\(?관리감독자\)?/g, '') : '-'}
                </div>
              </div>



              {/* 수시평가 실시 사유 */}
              {initialData.type === '수시' && initialData.reason && (
                <div className="col-md-12">
                  <label style={{ fontSize: '0.88rem', fontWeight: 800, color: '#0f172a', marginBottom: 8, display: 'block' }}>
                    <i className="fas fa-comment-dots" style={{ color: 'var(--sf-primary)', marginRight: 6 }}></i>수시평가 실시 사유
                  </label>
                  <div style={{ padding: '10px 14px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: '0.84rem', color: '#475569', minHeight: '60px', whiteSpace: 'pre-wrap', fontWeight: 500 }}>
                    {initialData.reason}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* 하단 버튼 */}
          <div style={{
            padding: '18px 28px', borderTop: '1px solid var(--sf-border-light)',
            background: '#f8fafc', display: 'flex', justifyContent: 'flex-end', gap: 10,
            borderRadius: '0 0 20px 20px'
          }}>
            {(() => {
              if (!hasPermission) return null;
              const status = initialData.status || 'in_progress';
              const btnBase = {
                border: 'none',
                borderRadius: '8px',
                fontWeight: 700,
                padding: '9px 12px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'all 0.2s',
                cursor: 'pointer',
                color: '#fff',
                fontSize: '0.9rem'
              };

              if (status === 'in_progress') {
                return (
                  <>
                    <button type="button" className="sf-btn" style={{ ...btnBase, background: '#6366f1' }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = '#4f46e5'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = '#6366f1'; }}
                      onClick={() => { if (onEdit) onEdit(initialData); }}
                    >수정</button>
                    <button type="button" className="sf-btn" style={{ ...btnBase, background: '#10b981' }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = '#059669'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = '#10b981'; }}
                      onClick={() => handleStatusChange(initialData.id, 'completed', '완료')}
                    >완료처리</button>
                    <button type="button" className="sf-btn" style={{ ...btnBase, background: '#f59e0b' }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = '#d97706'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = '#f59e0b'; }}
                      onClick={() => handleStatusChange(initialData.id, 'canceled', '취소')}
                    >취소처리</button>
                    <button type="button" className="sf-btn" style={{ ...btnBase, background: '#ef4444' }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = '#dc2626'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = '#ef4444'; }}
                      onClick={() => handleStatusChange(initialData.id, 'deleted', '삭제')}
                    >삭제처리</button>
                  </>
                );
              } else if (status === 'completed') {
                return (
                  <>
                    <button type="button" className="sf-btn" style={{ ...btnBase, background: '#6366f1' }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = '#4f46e5'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = '#6366f1'; }}
                      onClick={() => { if (onEdit) onEdit(initialData); }}
                    >수정</button>
                    <button type="button" className="sf-btn" style={{ ...btnBase, background: '#10b981' }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = '#059669'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = '#10b981'; }}
                      onClick={() => handleStatusChange(initialData.id, 'in_progress', '진행 재개')}
                    >진행 재개</button>
                    <button type="button" className="sf-btn" style={{ ...btnBase, background: '#f59e0b' }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = '#d97706'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = '#f59e0b'; }}
                      onClick={() => handleStatusChange(initialData.id, 'canceled', '취소')}
                    >취소처리</button>
                    <button type="button" className="sf-btn" style={{ ...btnBase, background: '#ef4444' }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = '#dc2626'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = '#ef4444'; }}
                      onClick={() => handleStatusChange(initialData.id, 'deleted', '삭제')}
                    >삭제처리</button>
                  </>
                );
              } else if (status === 'canceled') {
                return (
                  <button type="button" className="sf-btn" style={{ ...btnBase, background: '#6366f1' }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = '#4f46e5'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = '#6366f1'; }}
                    onClick={() => handleStatusChange(initialData.id, 'in_progress', '재등록')}
                  >재등록</button>
                );
              } else if (status === 'deleted') {
                return (
                  <>
                    <button type="button" className="sf-btn" style={{ ...btnBase, background: '#6366f1' }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = '#4f46e5'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = '#6366f1'; }}
                      onClick={() => handleStatusChange(initialData.id, 'in_progress', '재등록')}
                    >재등록</button>
                    <button type="button" className="sf-btn" style={{ ...btnBase, background: '#ef4444' }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = '#dc2626'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = '#ef4444'; }}
                      onClick={() => handleDelete(initialData.id)}
                    >완전삭제</button>
                  </>
                );
              }
              return null;
            })()}
            <button
              type="button"
              className="sf-btn"
              style={{
                border: 'none',
                background: '#94a3b8',
                color: '#fff',
                borderRadius: '8px',
                fontWeight: 700,
                padding: '9px 12px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'all 0.2s',
                cursor: 'pointer',
                fontSize: '0.9rem'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#64748b'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = '#94a3b8'; }}
              onClick={onClose}
            >
              닫기
            </button>
          </div>
        </div>

        {/* 에러 모달 (완료 처리 실패 등) */}
        {errorMessage && (
          <div style={{
            position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.55)', zIndex: 9999, backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }} onClick={(e) => { e.stopPropagation(); setErrorMessage(''); }}>
            <div style={{
              background: '#fff', borderRadius: '16px', padding: '36px 32px', maxWidth: '420px', width: '90%',
              boxShadow: '0 25px 50px -12px rgba(0,0,0,0.3)', textAlign: 'center',
              animation: 'fadeInUp 0.3s ease-out forwards'
            }} onClick={e => e.stopPropagation()}>
              <div style={{
                width: '72px', height: '72px', borderRadius: '50%', background: '#fee2e2',
                display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px',
                color: '#ef4444', fontSize: '2.2rem'
              }}>
                <i className="fas fa-exclamation-triangle"></i>
              </div>
              <h4 style={{ fontWeight: 800, color: '#0f172a', margin: '0 0 12px 0', fontSize: '1.25rem' }}>완료 처리 불가</h4>
              <p style={{ color: '#475569', fontSize: '0.95rem', margin: '0 0 30px 0', lineHeight: 1.6, wordBreak: 'keep-all' }}>
                {errorMessage}<br/><br/>
                조치 결과 메뉴에서 미완료 항목을 모두 <b>완료</b> 상태로 변경하신 후 다시 시도해 주세요.
              </p>
              <button
                type="button"
                onClick={() => setErrorMessage('')}
                style={{
                  background: '#ef4444', color: '#fff', border: 'none', borderRadius: '10px',
                  padding: '14px 24px', fontWeight: 800, cursor: 'pointer', width: '100%', fontSize: '1.05rem',
                  transition: 'background 0.2s', boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)'
                }}
                onMouseEnter={e => e.target.style.background = '#dc2626'}
                onMouseLeave={e => e.target.style.background = '#ef4444'}
              >
                확인
              </button>
            </div>
          </div>
        )}
      </div>,
      document.body
    );
  }

  // ===== 등록/수정 모드 =====
  return ReactDOM.createPortal(
    <div
      style={{
        position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
        backgroundColor: 'rgba(15, 23, 42, 0.7)', backdropFilter: 'blur(6px)',
        zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px'
      }}
      onClick={onClose}
    >
      <div
        style={{
          position: 'relative',
          width: '100%', maxWidth: '880px', maxHeight: '90vh',
          display: 'flex', flexDirection: 'column',
          background: '#fff', borderRadius: 'var(--sf-radius-xl)',
          boxShadow: '0 25px 50px rgba(0,0,0,0.25)', overflow: 'visible'
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* 모달 헤더 */}
        <div style={{
          background: 'var(--sf-gradient-primary)', padding: '24px 28px',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          borderRadius: 'var(--sf-radius-xl) var(--sf-radius-xl) 0 0'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <h4 style={{ fontWeight: 800, color: '#fff', margin: 0, fontSize: '1.3rem' }}>{formData.id ? '위험성평가 정보 수정' : '신규 위험성평가 등록'}</h4>
            <span className="sf-badge sf-badge-white" style={{ display: 'inline-block' }}>{formData.id ? '정보 수정' : '신규 등록'}</span>
          </div>
          <button
            onClick={onClose}
            style={{
              position: 'absolute', top: 0, right: 0,
              background: 'transparent', border: 'none',
              borderRadius: '0 var(--sf-radius-xl) 0 12px',
              width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', color: '#fff', fontSize: '1.2rem', zIndex: 10
            }}
          >
            <i className="fas fa-times"></i>
          </button>
        </div>

        {/* 모달 폼 */}
        <form onSubmit={handleSave} style={{ overflowY: 'visible', flex: 1 }}>
          <div style={{ padding: '28px' }}>
            <div className="row g-4">
              {/* 평가 구분 */}
              <div className="col-md-4">
                <label style={{ fontSize: '0.88rem', fontWeight: 800, color: '#0f172a', marginBottom: 8, display: 'block' }}>
                  <i className="fas fa-tag" style={{ color: 'var(--sf-primary)', marginRight: 6 }}></i>평가 구분 <span style={{ color: 'var(--sf-danger)' }}>*</span>
                </label>
                <select className="sf-input" value={formData.type} onChange={e => setFormData({ ...formData, type: e.target.value })}>
                  <option value="정기">정기 위험성평가</option>
                  <option value="수시">수시 위험성평가</option>
                  <option value="최초">최초 위험성평가</option>
                </select>
              </div>

              {/* 사업장명 */}
              <div className="col-md-8">
                <label style={{ fontSize: '0.88rem', fontWeight: 800, color: '#0f172a', marginBottom: 8, display: 'block' }}>
                  <i className="fas fa-building" style={{ color: 'var(--sf-primary)', marginRight: 6 }}></i>사업장명 <span style={{ color: 'var(--sf-danger)' }}>*</span>
                </label>
                <input className="sf-input" type="text" placeholder="예: 테크앤아이코리아" value={formData.siteName} onChange={e => setFormData({ ...formData, siteName: e.target.value })} required />
              </div>

              {/* 평가명 */}
              <div className={formData.id ? "col-md-9" : "col-md-12"}>
                <label style={{ fontSize: '0.88rem', fontWeight: 800, color: '#0f172a', marginBottom: 8, display: 'block' }}>
                  <i className="fas fa-clipboard-list" style={{ color: 'var(--sf-primary)', marginRight: 6 }}></i>위험성평가명 <span style={{ color: 'var(--sf-danger)' }}>*</span>
                </label>
                <input className="sf-input" type="text" placeholder={`예: ${new Date().getFullYear()} 테크앤아이코리아 사업장 정기 위험성평가`} value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} required />
              </div>

              {/* 진행 상태 (수정 모드일 때만 읽기전용으로 노출) */}
              {formData.id && (
                <div className="col-md-3">
                  <label style={{ fontSize: '0.88rem', fontWeight: 800, color: '#0f172a', marginBottom: 8, display: 'block' }}>
                    <i className="fas fa-info-circle" style={{ color: 'var(--sf-primary)', marginRight: 6 }}></i>진행 상태
                  </label>
                  <div style={getStatusBoxStyle(formData.status, true)}>
                    {getStatusLabel(formData.status)}
                  </div>
                </div>
              )}

              {/* 평가 기간 & 참여 근로자 수 */}
              <div className="col-md-12">
                <div style={{ display: 'flex', gap: '32px', alignItems: 'flex-start' }}>
                  <div>
                    <label style={{ fontSize: '0.88rem', fontWeight: 800, color: '#0f172a', marginBottom: 8, display: 'block' }}>
                      <i className="far fa-calendar-alt" style={{ color: 'var(--sf-primary)', marginRight: 6 }}></i>평가 실시 기간 <span style={{ color: 'var(--sf-danger)' }}>*</span>
                    </label>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <div className="date-picker-wrap" style={{ position: 'relative', width: '140px' }}>
                        <CommonDatePicker
                          popperPlacement="top-start"
                          value={formData.startDate ? new Date(formData.startDate) : null}
                          onChange={date => {
                            const formatted = date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '';
                            setFormData({ ...formData, startDate: formatted });
                          }}
                          placeholder="년-월-일"
                        />
                        <i className="fas fa-calendar-alt" style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: '#94a3b8' }}></i>
                      </div>
                      <span>~</span>
                      <div className="date-picker-wrap" style={{ position: 'relative', width: '140px' }}>
                        <CommonDatePicker
                          popperPlacement="top-start"
                          value={formData.endDate ? new Date(formData.endDate) : null}
                          onChange={date => {
                            const formatted = date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '';
                            setFormData({ ...formData, endDate: formatted });
                          }}
                          placeholder="년-월-일"
                        />
                        <i className="fas fa-calendar-alt" style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: '#94a3b8' }}></i>
                      </div>
                    </div>
                  </div>

                  <div style={{ width: '180px' }}>
                    <label style={{ fontSize: '0.88rem', fontWeight: 800, color: '#0f172a', marginBottom: 8, display: 'block' }}>
                      <i className="fas fa-users" style={{ color: 'var(--sf-primary)', marginRight: 6 }}></i>참여 근로자 수 (명)
                    </label>
                    <input className="sf-input" type="number" placeholder="예: 0" value={formData.workerCnt || ''} onChange={e => setFormData({ ...formData, workerCnt: e.target.value })} />
                  </div>
                </div>
              </div>

              {/* 안전관리자(총괄담당자) 지정 — OrgTreeSelect 연동 */}
              <div className="col-md-10">
                <label style={{ fontSize: '0.88rem', fontWeight: 800, color: '#0f172a', marginBottom: 8, display: 'block' }}>
                  <i className="fas fa-id-badge" style={{ color: 'var(--sf-primary)', marginRight: 6 }}></i>안전관리자 (평가 총괄 담당자)
                </label>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <input
                    className="sf-input"
                    type="text"
                    value={formData.managerName || ''}
                    placeholder="조직도에서 선택해 주세요"
                    readOnly
                    style={{ flex: 1, cursor: 'pointer', background: 'var(--sf-bg)' }}
                    onClick={() => setShowOrgPopup(true)}
                  />
                  <button
                    type="button"
                    className="sf-btn sf-btn-accent sf-btn-sm"
                    onClick={() => setShowOrgPopup(true)}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    <i className="fas fa-sitemap"></i> 조직도 선택
                  </button>
                  {formData.managerName && (
                    <button
                      type="button"
                      className="sf-btn sf-btn-outline sf-btn-sm"
                      onClick={() => setFormData({ ...formData, managerId: '', managerName: '' })}
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      초기화
                    </button>
                  )}
                </div>
              </div>

              {/* 수시평가 사유 (수시일 때만 표시) */}
              {formData.type === '수시' && (
                <div className="col-md-12">
                  <label style={{ fontSize: '0.88rem', fontWeight: 800, color: '#0f172a', marginBottom: 8, display: 'block' }}>
                    <i className="fas fa-comment-dots" style={{ color: 'var(--sf-primary)', marginRight: 6 }}></i>수시평가 실시 사유
                  </label>
                  <textarea
                    className="sf-input"
                    rows={3}
                    placeholder="신규 설비 도입, 중대재해 발생, 작업공정 변경 등"
                    value={formData.reason}
                    onChange={e => setFormData({ ...formData, reason: e.target.value })}
                    style={{ resize: 'vertical' }}
                  />
                </div>
              )}
            </div>
          </div>

          <div style={{
            padding: '18px 28px', borderTop: '1px solid var(--sf-border-light)',
            background: '#f8fafc', display: 'flex', justifyContent: 'flex-end', gap: 10,
            borderRadius: '0 0 20px 20px'
          }}>
            <button
              type="submit"
              className="sf-btn"
              style={{
                background: 'transparent',
                border: '1.5px solid var(--sf-primary)',
                color: 'var(--sf-primary)',
                borderRadius: '8px',
                fontWeight: 700,
                padding: '8px 15px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => { e.target.style.background = 'rgba(99, 102, 241, 0.05)'; }}
              onMouseLeave={(e) => { e.target.style.background = 'transparent'; }}
            >
              <i className={formData.id ? "fas fa-check" : "fas fa-plus"}></i> {formData.id ? '수정하기' : '등록하기'}
            </button>
            <button
              type="button"
              className="sf-btn"
              style={{
                background: 'transparent',
                border: '1.5px solid #cbd5e1',
                color: '#64748b',
                borderRadius: '8px',
                fontWeight: 700,
                padding: '8px 15px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => { e.target.style.background = '#f1f5f9'; }}
              onMouseLeave={(e) => { e.target.style.background = 'transparent'; }}
              onClick={onClose}
            >
              <i className="fas fa-times"></i> 닫기
            </button>
          </div>
        </form>
      </div>

      {/* OrgTreeSelect 팝업 (모달 위에 렌더링) */}
      {showOrgPopup && (
        <div onClick={e => e.stopPropagation()}>
          <OrgTreeSelect
            selectionMode="single"
            targetType="user"
            initialSelected={formData.managerId ? (() => {
              const fullName = formData.managerName || '';
              const regex = /^([가-힣a-zA-Z0-9]+)(?:\s+([가-힣a-zA-Z0-9]+))?(?:\((.+)\))?$/;
              const match = fullName.trim().match(regex);
              let name = fullName;
              let position = '';
              let dept = '';
              let disp = fullName;
              if (match) {
                name = match[1] || '';
                position = match[2] || '';
                dept = match[3] || '';
                disp = `${name}${position ? ' / ' + position : ''}${dept ? ' (' + dept + ')' : ''}`;
              }
              return [{
                target_type: 'user',
                target_id: formData.managerId,
                target_name: name,
                position: position,
                dept_name: dept,
                disp_target_name: disp
              }];
            })() : []}
            onConfirm={handleOrgConfirm}
            onCancel={() => setShowOrgPopup(false)}
          />
        </div>
      )}
    </div>,
    document.body
  );
}

export default SafetyProjectModal;
